import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useT } from "@/i18n";

const Header = () => {
  const { lang, setLang, t } = useT();
  const NAV = [
    { id: "product", label: t.nav.product },
    { id: "system", label: t.nav.system },
    { id: "resources", label: t.nav.resources },
    { id: "montreal", label: t.nav.montreal },
    { id: "quote", label: t.nav.quote },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src="https://milysuperior.com/wp-content/uploads/2023/10/cropped-cropped-Mily-Superior-Logo-1-119x84.png"
            alt="Mily Superior"
            className="h-11 w-auto"
          />
          <div className="leading-none hidden sm:block">
            <div className="font-display text-base tracking-wide">MILY SUPERIOR</div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-1">Construction Materials</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="text-sm text-zinc-700 hover:text-[#14141a] copper-line transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {/* EN/FR Toggle */}
          <div
            data-testid="lang-toggle"
            className="flex items-center border border-black/20 text-xs font-bold tracking-wider"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-2 transition-colors ${lang === "en" ? "bg-[#14141a] text-[#faf7f2]" : "text-[#14141a] hover:bg-black/5"}`}
              aria-label="English"
            >
              EN
            </button>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-3 py-2 transition-colors ${lang === "fr" ? "bg-[#14141a] text-[#faf7f2]" : "text-[#14141a] hover:bg-black/5"}`}
              aria-label="Français"
            >
              FR
            </button>
          </div>

          <a
            href="tel:5145941482"
            data-testid="header-call-button"
            className="inline-flex items-center gap-2 bg-[#ea7a1f] hover:bg-[#f08b34] text-white font-bold px-5 py-3 transition-all duration-300 hover:translate-y-[-2px]"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            <span className="text-sm tracking-wide">514-594-1482</span>
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#14141a]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 border-t border-black/10 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => setOpen(false)}
                className="text-zinc-700 text-lg font-display"
              >
                {n.label}
              </a>
            ))}
            <a
              href="tel:5145941482"
              data-testid="mobile-call-button"
              className="inline-flex items-center justify-center gap-2 bg-[#ea7a1f] text-white font-bold px-5 py-3"
            >
              <Phone className="w-4 h-4" />
              {lang === "fr" ? "Appelez" : "Call"} 514-594-1482
            </a>
            {/* EN/FR Toggle Mobile */}
            <div className="flex items-center justify-center gap-2 pt-2 border-t border-black/10">
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLang("en")}
                className={`px-4 py-2 text-xs font-bold tracking-widest ${lang === "en" ? "bg-[#14141a] text-[#faf7f2]" : "text-[#14141a] border border-black/20"}`}
              >EN</button>
              <button
                data-testid="mobile-lang-fr"
                onClick={() => setLang("fr")}
                className={`px-4 py-2 text-xs font-bold tracking-widest ${lang === "fr" ? "bg-[#14141a] text-[#faf7f2]" : "text-[#14141a] border border-black/20"}`}
              >FR</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
